import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  ListPlus,
  ShieldCheck,
  Layers,
  Users,
  Settings2,
  Coins,
} from "lucide-react";

import {
  CURRENCY_CONFIG,
  EmptyState,
  GlassCard,
  PERMISSION,
  PRIORITY_COLORS,
  PageHeader,
  PageShell,
  PersonAvatar,
  T,
  can,
  formatCurrency,
  formatDuration,
  ghostButtonSx,
  primaryButtonSx,
} from "./shared";
import {
  addSubCategory,
  assignRole,
  createCategory,
  deleteCategory,
  errorMessage,
  fetchAssignableMembers,
  fetchCategories,
  fetchMyPermissions,
  fetchRoleAssignments,
  fetchRoleCatalogue,
  fetchSettings,
  removeSubCategory,
  revokeRole,
  seedCategories,
  updateCategory,
  updateSettings,
} from "./api";

const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const ComplaintSettings = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [me, setMe] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState("");

  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null, values: {} });
  const [subDialog, setSubDialog] = useState({ open: false, category: null, name: "" });
  const [roleDialog, setRoleDialog] = useState({ open: false, values: {} });

  // Workspace settings (currency, agent hourly rate) and their unsaved draft.
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({ currency: "INR", costPerAgentHour: 250 });

  const permissions = me?.permissions || [];
  const notify = (message, severity = "success") => setToast({ open: true, message, severity });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const permissionData = await fetchMyPermissions();
      setMe(permissionData);

      const tasks = [fetchCategories(true), fetchSettings()];
      const canManageRoles = permissionData.permissions?.includes(PERMISSION.ROLE_MANAGE);

      if (canManageRoles) {
        tasks.push(fetchRoleCatalogue(), fetchRoleAssignments(), fetchAssignableMembers());
      }

      const [categoryData, settingsData, catalogueData, assignmentData, memberData] =
        await Promise.all(tasks);

      setCategories(categoryData || []);
      setSettings(settingsData);
      setSettingsDraft({
        currency: settingsData?.currency || "INR",
        costPerAgentHour: settingsData?.costPerAgentHour ?? 250,
      });
      if (canManageRoles) {
        setCatalogue(catalogueData);
        setAssignments(assignmentData || []);
        setMembers(memberData || []);
      }
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Failed to load settings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /** Runs a mutation, reports the outcome and refreshes. */
  const run = async (action, fallback) => {
    setBusy(true);
    try {
      const response = await action();
      notify(response?.message || fallback);
      await loadAll();
      return true;
    } catch (err) {
      notify(errorMessage(err, "Action failed"), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // -- category dialog -----------------------------------------------------
  const openCategoryDialog = (category = null) => {
    setCategoryDialog({
      open: true,
      category,
      values: category
        ? {
            name: category.name,
            defaultPriority: category.defaultPriority,
            department: category.department,
            defaultQueue: category.defaultQueue,
            slaMatrix: JSON.parse(JSON.stringify(category.slaMatrix || {})),
          }
        : {
            name: "",
            defaultPriority: "Medium",
            department: "customer_support",
            defaultQueue: "l1_support",
            slaMatrix: PRIORITIES.reduce(
              (acc, p) => ({ ...acc, [p]: { firstResponseMins: 60, resolutionMins: 1440 } }),
              {}
            ),
          },
    });
  };

  const setCategoryValue = (key, value) =>
    setCategoryDialog((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));

  const setSlaValue = (priority, field, value) =>
    setCategoryDialog((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        slaMatrix: {
          ...prev.values.slaMatrix,
          [priority]: { ...prev.values.slaMatrix[priority], [field]: Number(value) || 0 },
        },
      },
    }));

  const saveCategory = async () => {
    const { category, values } = categoryDialog;
    const ok = await run(
      () => (category ? updateCategory(category._id, values) : createCategory(values)),
      category ? "Category updated" : "Category created"
    );
    if (ok) setCategoryDialog({ open: false, category: null, values: {} });
  };

  // -- role dialog ---------------------------------------------------------
  const saveRole = async () => {
    const ok = await run(() => assignRole(roleDialog.values), "Role assigned");
    if (ok) setRoleDialog({ open: false, values: {} });
  };

  if (loading) {
    return (
      <PageShell>
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress size={28} />
        </Box>
      </PageShell>
    );
  }

  const canManageCategories = can(permissions, PERMISSION.CATEGORY_MANAGE);
  const canManageRoles = can(permissions, PERMISSION.ROLE_MANAGE);
  const canManageSettings = can(permissions, PERMISSION.SETTINGS_MANAGE);

  // The Roles tab only exists for role managers, so General shifts index.
  const generalTab = canManageRoles ? 2 : 1;

  const currencyChanged = Boolean(settings) && settingsDraft.currency !== settings.currency;
  const settingsDirty =
    Boolean(settings) &&
    (currencyChanged ||
      Number(settingsDraft.costPerAgentHour) !== Number(settings.costPerAgentHour));

  return (
    <>
      <PageShell>
            <PageHeader
              icon={Settings2}
              title="Complaint Module Settings"
              subtitle="Categories, SLA targets and who can do what."
              actions={
                <IconButton
                  size="small"
                  onClick={() => navigate("/complaints")}
                  sx={{ border: `1px solid ${T.border}`, borderRadius: T.radiusControl }}
                >
                  <ArrowLeft size={16} color={T.inkSecondary} />
                </IconButton>
              }
            />

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: T.radiusControl }}>{error}</Alert>}

            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              sx={{
                mb: 2,
                minHeight: 42,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  minHeight: 42,
                  color: T.inkSecondary,
                },
                "& .Mui-selected": { color: `${T.brand} !important` },
                "& .MuiTabs-indicator": { bgcolor: T.brand, height: 2.5, borderRadius: 2 },
              }}
            >
              <Tab icon={<Layers size={15} />} iconPosition="start" label="Categories & SLA" />
              {canManageRoles && <Tab icon={<Users size={15} />} iconPosition="start" label="Roles & Permissions" />}
              <Tab icon={<Coins size={15} />} iconPosition="start" label="General" />
            </Tabs>

            {/* ---- Categories ---- */}
            {tab === 0 && (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  {canManageCategories && (
                    <>
                      <Button
                        variant="contained" size="small" startIcon={<Plus size={16} />}
                        onClick={() => openCategoryDialog()}
                        sx={primaryButtonSx}
                      >
                        New Category
                      </Button>
                      <Tooltip title="Creates the 12 standard food-delivery categories with their sub-categories and SLA targets. Existing categories are left alone.">
                        <Button
                          variant="outlined" size="small" startIcon={<ListPlus size={16} />} disabled={busy}
                          onClick={() => run(seedCategories, "Standard categories loaded")}
                          sx={ghostButtonSx}
                        >
                          Load Standard Categories
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {categories.length === 0 ? (
                  <GlassCard>
                    <EmptyState
                      icon={Layers}
                      title="No categories yet"
                      description="Load the 12 standard food-delivery categories with their sub-categories and SLA targets, or create your own."
                      action={
                        canManageCategories && (
                          <Button
                            variant="contained" size="small" startIcon={<ListPlus size={16} />}
                            onClick={() => run(seedCategories, "Standard categories loaded")}
                            sx={primaryButtonSx}
                          >
                            Load Standard Categories
                          </Button>
                        )
                      }
                    />
                  </GlassCard>
                ) : (
                  <Grid container spacing={2}>
                    {categories.map((category) => (
                      <Grid item xs={12} md={6} key={category._id}>
                        <GlassCard sx={{ p: 2.25, height: "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                    bgcolor: PRIORITY_COLORS[category.defaultPriority]?.dot || T.inkMuted,
                                  }}
                                />
                                <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: T.ink }}>
                                  {category.name}
                                </Typography>
                                {!category.isActive && (
                                  <Chip
                                    label="Inactive"
                                    size="small"
                                    sx={{ height: 19, fontSize: "0.62rem", borderRadius: "999px" }}
                                  />
                                )}
                              </Stack>
                              <Typography sx={{ fontSize: "0.72rem", color: T.inkSecondary, mt: 0.25 }}>
                                Default priority {category.defaultPriority} ·{" "}
                                {catalogue?.departments?.find((d) => d.key === category.department)?.label || category.department}
                              </Typography>
                            </Box>
                            {canManageCategories && (
                              <Box>
                                <Tooltip title="Edit category and SLA">
                                  <IconButton size="small" onClick={() => openCategoryDialog(category)}>
                                    <Pencil size={15} color={T.inkSecondary} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete (deactivates if complaints already use it)">
                                  <IconButton
                                    size="small"
                                    onClick={() => run(() => deleteCategory(category._id), "Category removed")}
                                  >
                                    <Trash2 size={15} color={T.inkSecondary} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>

                          <Divider sx={{ my: 1.25, borderColor: T.border }} />

                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            SLA targets
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, mb: 1.5 }}>
                            {PRIORITIES.map((priority) => {
                              const target = category.slaMatrix?.[priority];
                              if (!target) return null;
                              return (
                                <Tooltip
                                  key={priority}
                                  title={`First response ${formatDuration(target.firstResponseMins)}, resolution ${formatDuration(target.resolutionMins)}`}
                                >
                                  <Chip
                                    label={`${priority}: ${formatDuration(target.resolutionMins)}`}
                                    size="small"
                                    sx={{ height: 22, fontSize: "0.68rem" }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Sub-categories ({category.subCategories?.length || 0})
                            </Typography>
                            {canManageCategories && (
                              <Button
                                size="small"
                                onClick={() => setSubDialog({ open: true, category, name: "" })}
                                sx={{ textTransform: "none", fontSize: "0.72rem", color: T.brand, fontWeight: 600 }}
                              >
                                Add
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {(category.subCategories || []).map((sub) => (
                              <Chip
                                key={sub}
                                label={sub}
                                size="small"
                                sx={{ height: 22, fontSize: "0.68rem" }}
                                onDelete={
                                  canManageCategories
                                    ? () => run(() => removeSubCategory(category._id, sub), "Sub-category removed")
                                    : undefined
                                }
                              />
                            ))}
                          </Box>
                        </GlassCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {/* ---- Roles ---- */}
            {tab === 1 && canManageRoles && (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button
                    variant="contained" size="small" startIcon={<ShieldCheck size={16} />}
                    onClick={() => setRoleDialog({ open: true, values: { role: "", department: "customer_support", queues: [] } })}
                    sx={primaryButtonSx}
                  >
                    Assign Role
                  </Button>
                </Box>

                <GlassCard sx={{ mb: 3, overflow: "hidden" }}>
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.border}` }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink }}>
                      Assigned People
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["Name", "Email", "Role", "Department", "Team", "Refund Limit", ""].map((header) => (
                            <TableCell key={header} sx={{ fontWeight: 700, bgcolor: T.surfaceMuted, fontSize: "0.7rem", color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignments.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography variant="caption" color="text.secondary">
                                Nobody assigned yet. Team members need a role here before they can open the module.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          assignments.map((assignment) => (
                            <TableRow key={assignment._id} hover>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <PersonAvatar name={assignment.memberName} size={26} />
                                  <span>{assignment.memberName}</span>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", color: T.inkSecondary }}>
                                {assignment.memberEmail}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                <Chip
                                  label={assignment.roleLabel}
                                  size="small"
                                  sx={{
                                    height: 22, fontSize: "0.7rem", fontWeight: 600,
                                    borderRadius: "999px", bgcolor: T.brandSoft, color: T.brand,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{assignment.departmentLabel}</TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{assignment.team || "—"}</TableCell>
                              <TableCell sx={{ fontSize: "0.8rem", fontVariantNumeric: "tabular-nums" }}>
                                {assignment.effectiveRefundLimit === null
                                  ? "Unlimited"
                                  : formatCurrency(assignment.effectiveRefundLimit)}
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Revoke complaint-module access">
                                  <IconButton
                                    size="small"
                                    onClick={() => run(() => revokeRole(assignment._id), "Access revoked")}
                                  >
                                    <Trash2 size={15} color={T.inkSecondary} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </GlassCard>

                <GlassCard sx={{ overflow: "hidden" }}>
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${T.border}` }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink }}>
                      Role Reference
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: T.inkSecondary }}>
                      What each role can see and approve.
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {["Role", "Visibility", "Refund Limit", "Permissions"].map((header) => (
                            <TableCell key={header} sx={{ fontWeight: 700, bgcolor: T.surfaceMuted, fontSize: "0.7rem", color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.3px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(catalogue?.roles || []).map((role) => (
                          <TableRow key={role.role} hover>
                            <TableCell sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{role.label}</TableCell>
                            <TableCell sx={{ fontSize: "0.78rem" }}>
                              {role.scope}
                              {role.closedOnly && " (closed tickets only)"}
                              {role.categoryScope && ` · ${role.categoryScope.join(", ")}`}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.78rem" }}>
                              {role.refundLimit === null ? "Unlimited" : role.refundLimit === 0 ? "None" : formatCurrency(role.refundLimit)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.78rem" }}>{role.permissionCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </GlassCard>
              </>
            )}

            {/* ---- General ---- */}
            {tab === generalTab && (
              <GlassCard sx={{ maxWidth: 620 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${T.border}` }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.ink }}>
                    Currency &amp; Costing
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: T.inkSecondary, mt: 0.25 }}>
                    Applies to every amount in this workspace — refunds, order values and reports.
                  </Typography>
                </Box>

                <Box sx={{ p: 2.5 }}>
                  <TextField
                    select fullWidth size="small" label="Currency" sx={{ mb: 2 }}
                    value={settingsDraft.currency}
                    disabled={!canManageSettings}
                    onChange={(e) =>
                      setSettingsDraft((p) => ({ ...p, currency: e.target.value }))
                    }
                  >
                    {Object.values(CURRENCY_CONFIG).map((currency) => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {currency.symbol} &nbsp;{currency.code} — {currency.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth size="small" type="number" label="Agent hourly rate"
                    value={settingsDraft.costPerAgentHour}
                    disabled={!canManageSettings}
                    onChange={(e) =>
                      setSettingsDraft((p) => ({ ...p, costPerAgentHour: e.target.value }))
                    }
                    helperText="Used by the Cost of Resolution report to value agent handling time."
                  />

                  {currencyChanged && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: T.radiusControl }}>
                      Existing amounts are <strong>relabelled, not converted</strong>. Figures
                      recorded as {settings?.currency} will display as {settingsDraft.currency} at
                      the same numeric value.
                    </Alert>
                  )}

                  {canManageSettings && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                      <Button
                        variant="contained" size="small" disabled={busy || !settingsDirty}
                        onClick={() =>
                          run(
                            () =>
                              updateSettings({
                                currency: settingsDraft.currency,
                                costPerAgentHour: Number(settingsDraft.costPerAgentHour),
                              }),
                            "Settings updated"
                          )
                        }
                        sx={primaryButtonSx}
                      >
                        Save Changes
                      </Button>
                      {settingsDirty && (
                        <Button
                          size="small" disabled={busy} sx={ghostButtonSx} variant="outlined"
                          onClick={() =>
                            setSettingsDraft({
                              currency: settings?.currency || "INR",
                              costPerAgentHour: settings?.costPerAgentHour ?? 250,
                            })
                          }
                        >
                          Discard
                        </Button>
                      )}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2.5, borderColor: T.border }} />
                  <Typography sx={{ fontSize: "0.75rem", color: T.inkSecondary }}>
                    Preview: a refund of 1250 shows as{" "}
                    <strong>{formatCurrency(1250, settingsDraft.currency)}</strong>
                  </Typography>
                </Box>
              </GlassCard>
            )}
      </PageShell>

      {/* ---- Category dialog ---- */}
      <Dialog
        open={categoryDialog.open}
        onClose={busy ? undefined : () => setCategoryDialog({ open: false, category: null, values: {} })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={700}>
            {categoryDialog.category ? "Edit Category" : "New Category"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth size="small" label="Category name" sx={{ mb: 2 }}
            value={categoryDialog.values.name || ""}
            onChange={(e) => setCategoryValue("name", e.target.value)}
          />
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth size="small" label="Default priority"
                value={categoryDialog.values.defaultPriority || "Medium"}
                onChange={(e) => setCategoryValue("defaultPriority", e.target.value)}
              >
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth size="small" label="Owning department"
                value={categoryDialog.values.department || "customer_support"}
                onChange={(e) => setCategoryValue("department", e.target.value)}
              >
                {(catalogue?.departments || [{ key: "customer_support", label: "Customer Support" }]).map((d) => (
                  <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select fullWidth size="small" label="Default queue"
                value={categoryDialog.values.defaultQueue || "l1_support"}
                onChange={(e) => setCategoryValue("defaultQueue", e.target.value)}
              >
                {(catalogue?.queues || [{ key: "l1_support", label: "L1 Support Agent" }]).map((q) => (
                  <MenuItem key={q.key} value={q.key}>{q.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            SLA targets (minutes from registration)
          </Typography>

          {PRIORITIES.map((priority) => (
            <Grid container spacing={1} key={priority} sx={{ mt: 0.5 }} alignItems="center">
              <Grid item xs={4}>
                <Typography variant="body2" fontWeight={600}>{priority}</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" type="number" label="First response"
                  value={categoryDialog.values.slaMatrix?.[priority]?.firstResponseMins ?? ""}
                  onChange={(e) => setSlaValue(priority, "firstResponseMins", e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" type="number" label="Resolution"
                  value={categoryDialog.values.slaMatrix?.[priority]?.resolutionMins ?? ""}
                  onChange={(e) => setSlaValue(priority, "resolutionMins", e.target.value)}
                />
              </Grid>
            </Grid>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setCategoryDialog({ open: false, category: null, values: {} })}
            disabled={busy} sx={{ textTransform: "none", color: T.inkSecondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" onClick={saveCategory} disabled={busy}
            sx={primaryButtonSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Sub-category dialog ---- */}
      <Dialog
        open={subDialog.open}
        onClose={busy ? undefined : () => setSubDialog({ open: false, category: null, name: "" })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={700}>
            Add Sub-category to {subDialog.category?.name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth size="small" label="Sub-category name"
            value={subDialog.name}
            onChange={(e) => setSubDialog((p) => ({ ...p, name: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSubDialog({ open: false, category: null, name: "" })} disabled={busy} sx={{ textTransform: "none", color: T.inkSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained" disabled={busy || !subDialog.name.trim()}
            onClick={async () => {
              const ok = await run(() => addSubCategory(subDialog.category._id, subDialog.name), "Sub-category added");
              if (ok) setSubDialog({ open: false, category: null, name: "" });
            }}
            sx={primaryButtonSx}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Role assignment dialog ---- */}
      <Dialog
        open={roleDialog.open}
        onClose={busy ? undefined : () => setRoleDialog({ open: false, values: {} })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={700}>Assign Complaint Role</Typography>
          <Typography variant="caption" color="text.secondary">
            Assigning an existing person a new role replaces their current one.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            select fullWidth size="small" label="Team member" sx={{ mb: 2 }}
            value={roleDialog.values.memberId || ""}
            onChange={(e) => setRoleDialog((p) => ({ ...p, values: { ...p.values, memberId: e.target.value } }))}
          >
            {members.map((member) => (
              <MenuItem key={member.memberId} value={member.memberId}>
                {member.name} ({member.email}){member.hasComplaintRole ? " — already assigned" : ""}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Complaint role" sx={{ mb: 2 }}
            value={roleDialog.values.role || ""}
            onChange={(e) => setRoleDialog((p) => ({ ...p, values: { ...p.values, role: e.target.value } }))}
            helperText={
              catalogue?.roles?.find((r) => r.role === roleDialog.values.role)
                ? `Visibility: ${catalogue.roles.find((r) => r.role === roleDialog.values.role).scope}`
                : " "
            }
          >
            {(catalogue?.roles || []).map((role) => (
              <MenuItem key={role.role} value={role.role}>{role.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Department" sx={{ mb: 2 }}
            value={roleDialog.values.department || "customer_support"}
            onChange={(e) => setRoleDialog((p) => ({ ...p, values: { ...p.values, department: e.target.value } }))}
          >
            {(catalogue?.departments || []).map((d) => (
              <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Queues handled" sx={{ mb: 2 }}
            SelectProps={{ multiple: true }}
            value={roleDialog.values.queues || []}
            onChange={(e) => setRoleDialog((p) => ({ ...p, values: { ...p.values, queues: e.target.value } }))}
            helperText="Used for team visibility and forwarding"
          >
            {(catalogue?.queues || []).map((q) => (
              <MenuItem key={q.key} value={q.key}>{q.label}</MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Team (optional)"
                value={roleDialog.values.team || ""}
                onChange={(e) => setRoleDialog((p) => ({ ...p, values: { ...p.values, team: e.target.value } }))}
                helperText="e.g. Night Shift"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="number" label="Refund limit override"
                value={roleDialog.values.refundLimitOverride ?? ""}
                onChange={(e) =>
                  setRoleDialog((p) => ({
                    ...p,
                    values: { ...p.values, refundLimitOverride: e.target.value === "" ? null : e.target.value },
                  }))
                }
                helperText="Blank uses the role default"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRoleDialog({ open: false, values: {} })} disabled={busy} sx={{ textTransform: "none", color: T.inkSecondary }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={saveRole}
            disabled={busy || !roleDialog.values.memberId || !roleDialog.values.role}
            sx={primaryButtonSx}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity} variant="filled"
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "12px", fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ComplaintSettings;
